import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, CheckCircle2, Baby, Camera } from "lucide-react";
import { AdminShell } from "../components/AdminShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, apiError, money } from "../lib/api";
import { toast } from "sonner";

const newAdult = () => ({ full_name: "", email: "", phone: "", use_contact_data: true, delegation_id: "", sector_id: "", general_event: false, dinner: false, workshops: [], photoFile: null });
const newChild = () => ({ full_name: "", age: "", care_required: true, authorized: [], photoFile: null, allergies: "", medical_notes: "" });

const ManualContent = () => {
  const [catalog, setCatalog] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [pricing, setPricing] = useState({});
  const [groupType, setGroupType] = useState("individual");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", groupName: "" });
  const [adults, setAdults] = useState([newAdult()]);
  const [children, setChildren] = useState([]);
  const [payment, setPayment] = useState({ mode: "pending", amount: "", reference: "", notes: "" });
  const [reason, setReason] = useState("");
  const [markTest, setMarkTest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/public/catalog"), api.get("/admin/workshops"), api.get("/admin/pricing")])
      .then(([c, w, p]) => {
        setCatalog(c.data.delegations || []);
        setWorkshops((w.data.workshops || []).filter((x) => x.status === "published" && x.starts_at && x.ends_at));
        const map = {};
        (p.data.pricing || []).forEach((i) => (map[i.code] = i));
        setPricing(map);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const updateAdult = (i, patch) => setAdults((l) => l.map((a, x) => (x === i ? { ...a, ...patch } : a)));
  const updateChild = (i, patch) => setChildren((l) => l.map((c, x) => (x === i ? { ...c, ...patch } : c)));

  const careFee = pricing.child_care_fee?.amount || 0;
  const total = adults.reduce((s, a) => {
    let t = (a.general_event || a.dinner || a.workshops.length > 0) ? pricing.general_event?.amount || 0 : 0;
    if (a.dinner) t += pricing.dinner?.amount || 0;
    a.workshops.forEach((id) => { t += workshops.find((w) => w.id === id)?.price || 0; });
    return s + t;
  }, 0) + children.filter((c) => c.care_required).length * careFee;

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("photo", file);
    const { data } = await api.post("/registrations/photos", fd);
    return data.photo_path;
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const adultsPayload = [];
      for (let i = 0; i < adults.length; i++) {
        const a = adults[i];
        if (!a.photoFile) throw new Error(`El adulto ${i + 1} requiere fotografía.`);
        adultsPayload.push({
          full_name: a.full_name.trim(),
          email: a.use_contact_data ? null : a.email.trim() || null,
          phone: a.use_contact_data ? null : a.phone || null,
          use_contact_data: a.use_contact_data,
          delegation_id: a.delegation_id, sector_id: a.sector_id,
          general_event: a.general_event || a.dinner || a.workshops.length > 0,
          dinner: a.dinner, workshop_ids: a.workshops,
          photo_path: await uploadPhoto(a.photoFile),
        });
      }
      const childrenPayload = [];
      for (let i = 0; i < children.length; i++) {
        const c = children[i];
        if (!c.photoFile) throw new Error(`El niño ${i + 1} requiere fotografía.`);
        childrenPayload.push({
          full_name: c.full_name.trim(),
          photo_path: await uploadPhoto(c.photoFile),
          age: c.age ? parseInt(c.age, 10) : null,
          allergies: c.allergies || null, medical_notes: c.medical_notes || null,
          care_required: c.care_required,
          authorized_adult_indices: c.authorized,
          external_guardians: [],
        });
      }
      const { data } = await api.post("/admin/manual-registration", {
        group_type: groupType,
        group_name: contact.groupName.trim() || null,
        primary_contact_name: contact.name.trim(),
        primary_contact_email: contact.email.trim() || null,
        primary_contact_phone: contact.phone || null,
        adults: adultsPayload, children: childrenPayload,
        payment_mode: payment.mode,
        paid_amount: parseFloat(payment.amount) || 0,
        payment_reference: payment.reference.trim() || null,
        payment_notes: payment.notes.trim() || null,
        reason,
        mark_as_test: markTest,
      });
      setCreated(data);
      setAdults([newAdult()]);
      setChildren([]);
      setContact({ name: "", email: "", phone: "", groupName: "" });
      setPayment({ mode: "pending", amount: "", reference: "", notes: "" });
      setReason("");
      setMarkTest(false);
      toast.success("Registro creado correctamente");
    } catch (err) {
      toast.error(err.message?.startsWith("El ") ? err.message : apiError(err, "No se pudo crear el registro."));
    } finally {
      setSubmitting(false);
    }
  };

  const valid = contact.name.trim().length >= 3 && reason.trim().length >= 5
    && adults.every((a) => a.full_name.trim().length >= 3 && a.photoFile && a.delegation_id && a.sector_id)
    && children.every((c) => c.full_name.trim().length >= 3 && c.photoFile && c.authorized.length > 0);

  return (
    <div className="space-y-6" data-testid="manual-entry">
      <section className="rounded-2xl border border-hz-gray/40 bg-white p-6 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="font-semibold text-hz-ink">Tipo de registro *</Label>
            <Select value={groupType} onValueChange={(v) => { setGroupType(v); if (v === "group") setChildren([]); if (v === "individual") setAdults((l) => l.slice(0, 1)); }}>
              <SelectTrigger data-testid="manual-group-type" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual" data-testid="manual-type-individual">Individual</SelectItem>
                <SelectItem value="family" data-testid="manual-type-family">Familiar</SelectItem>
                <SelectItem value="group" data-testid="manual-type-group">Grupal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-semibold text-hz-ink">{groupType === "family" ? "Nombre de la familia" : "Nombre del grupo"} {groupType === "group" ? "*" : "(opcional)"}</Label>
            <Input data-testid="manual-group-name" value={contact.groupName} onChange={(e) => setContact({ ...contact, groupName: e.target.value })} className="mt-1" />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label className="font-semibold text-hz-ink">Contacto principal *</Label>
            <Input data-testid="manual-contact-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="font-semibold text-hz-ink">Correo de contacto</Label>
            <Input type="email" data-testid="manual-contact-email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="font-semibold text-hz-ink">Teléfono de contacto</Label>
            <Input data-testid="manual-contact-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="mt-1" />
          </div>
        </div>
      </section>

      <section className="space-y-4">
        {adults.map((a, i) => (
          <div key={i} className="space-y-4 rounded-2xl border border-hz-gray/40 bg-white p-6" data-testid={`manual-adult-${i}`}>
            <div className="flex items-center justify-between">
              <p className="font-bold text-hz-navy">Adulto {i + 1}</p>
              {adults.length > 1 && (
                <button onClick={() => setAdults((l) => l.filter((_, x) => x !== i))} data-testid={`manual-adult-remove-${i}`} className="text-xs font-bold text-hz-error hover:underline">
                  <Trash2 className="mr-1 inline h-3.5 w-3.5" /> Eliminar
                </button>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label className="text-xs font-semibold">Nombre completo *</Label>
                <Input data-testid={`manual-adult-name-${i}`} value={a.full_name} onChange={(e) => updateAdult(i, { full_name: e.target.value })} className="mt-1" />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
                  <Checkbox data-testid={`manual-adult-contact-${i}`} checked={a.use_contact_data} onCheckedChange={(v) => updateAdult(i, { use_contact_data: v === true })} />
                  Usar contacto del responsable
                </label>
              </div>
              <div>
                <Label className="text-xs font-semibold">Fotografía *</Label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <label className="flex cursor-pointer items-center justify-center gap-1 rounded-lg border border-dashed border-hz-gray/60 px-2 py-2 text-xs font-semibold text-hz-navy hover:border-hz-cyan">
                    <Camera className="h-4 w-4" /> Tomar foto
                    <input type="file" accept="image/*" capture="user" className="hidden" data-testid={`manual-adult-photo-camera-${i}`} onChange={(e) => updateAdult(i, { photoFile: e.target.files?.[0] || null })} />
                  </label>
                  <label className="flex cursor-pointer items-center justify-center gap-1 rounded-lg border border-dashed border-hz-gray/60 px-2 py-2 text-xs font-semibold text-hz-navy hover:border-hz-cyan">
                    {a.photoFile ? a.photoFile.name : "Subir imagen"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`manual-adult-photo-${i}`} onChange={(e) => updateAdult(i, { photoFile: e.target.files?.[0] || null })} />
                  </label>
                </div>
              </div>
            </div>
            {!a.use_contact_data && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-semibold">Correo (opcional)</Label>
                  <Input type="email" data-testid={`manual-adult-email-${i}`} value={a.email} onChange={(e) => updateAdult(i, { email: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Teléfono (opcional)</Label>
                  <Input data-testid={`manual-adult-phone-${i}`} value={a.phone} onChange={(e) => updateAdult(i, { phone: e.target.value })} className="mt-1" />
                </div>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-semibold">Delegación *</Label>
                <select value={a.delegation_id} onChange={(e) => updateAdult(i, { delegation_id: e.target.value, sector_id: "" })} data-testid={`manual-adult-delegation-${i}`} className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                  <option value="">Selecciona…</option>
                  {catalog.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Sector *</Label>
                <select value={a.sector_id} onChange={(e) => updateAdult(i, { sector_id: e.target.value })} data-testid={`manual-adult-sector-${i}`} className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                  <option value="">Selecciona…</option>
                  {(catalog.find((d) => d.id === a.delegation_id)?.sectors || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
                <Checkbox data-testid={`manual-adult-general-${i}`} checked={a.general_event || a.dinner || a.workshops.length > 0}
                  onCheckedChange={(v) => {
                    if (v !== true && (a.dinner || a.workshops.length > 0)) {
                      toast.error("La cena y los talleres requieren acceso al evento general del sábado.");
                      return;
                    }
                    updateAdult(i, { general_event: v === true });
                  }} />
                Evento general — sábado ({money(pricing.general_event?.amount || 0)})
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
                <Checkbox data-testid={`manual-adult-dinner-${i}`} checked={a.dinner}
                  onCheckedChange={(v) => updateAdult(i, v === true && !a.general_event ? { dinner: true, general_event: true } : { dinner: v === true })} />
                Cena especial ({money(pricing.dinner?.amount || 0)})
              </label>
              <div className="flex flex-wrap gap-3">
                {workshops.map((w) => (
                  <label key={w.id} className="flex items-center gap-1.5 text-xs font-medium text-hz-ink">
                    <Checkbox data-testid={`manual-adult-workshop-${i}-${w.id}`} checked={a.workshops.includes(w.id)}
                      onCheckedChange={(v) => updateAdult(i, v && !a.general_event
                        ? { workshops: [...a.workshops, w.id], general_event: true }
                        : { workshops: v ? [...a.workshops, w.id] : a.workshops.filter((x) => x !== w.id) })} />
                    {w.name} ({money(w.price || 0)})
                  </label>
                ))}
              </div>
            </div>
          </div>
        ))}
        {groupType !== "individual" && (
          <Button variant="outline" onClick={() => setAdults((l) => [...l, newAdult()])} data-testid="manual-add-adult" className="w-full border-dashed font-bold text-hz-navy">
            <Plus className="mr-2 h-4 w-4" /> Agregar adulto
          </Button>
        )}
      </section>

      {groupType !== "group" && (
        <section className="space-y-4">
          <p className="flex items-center gap-2 text-sm font-bold text-hz-navy"><Baby className="h-4 w-4 text-hz-cyan" /> Niños (cuidado infantil) — opcional{pricing.child_care_fee ? ` · ${money(careFee)} por niño` : ""}</p>
          {children.map((c, i) => (
            <div key={i} className="space-y-3 rounded-2xl border border-hz-gray/40 bg-white p-6" data-testid={`manual-child-${i}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-hz-navy">Niño {i + 1}</p>
                <button onClick={() => setChildren((l) => l.filter((_, x) => x !== i))} data-testid={`manual-child-remove-${i}`} className="text-xs font-bold text-hz-error hover:underline">
                  <Trash2 className="mr-1 inline h-3.5 w-3.5" /> Eliminar
                </button>
              </div>
              <div className="grid gap-3 sm:grid-cols-4">
                <div>
                  <Label className="text-xs font-semibold">Nombre *</Label>
                  <Input data-testid={`manual-child-name-${i}`} value={c.full_name} onChange={(e) => updateChild(i, { full_name: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Edad</Label>
                  <Input type="number" min="0" max="17" data-testid={`manual-child-age-${i}`} value={c.age} onChange={(e) => updateChild(i, { age: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Fotografía *</Label>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    <label className="flex cursor-pointer items-center justify-center gap-1 rounded-lg border border-dashed border-hz-gray/60 px-2 py-2 text-xs font-semibold text-hz-navy hover:border-hz-cyan">
                      <Camera className="h-4 w-4" /> Tomar foto
                      <input type="file" accept="image/*" capture="user" className="hidden" data-testid={`manual-child-photo-camera-${i}`} onChange={(e) => updateChild(i, { photoFile: e.target.files?.[0] || null })} />
                    </label>
                    <label className="flex cursor-pointer items-center justify-center gap-1 rounded-lg border border-dashed border-hz-gray/60 px-2 py-2 text-xs font-semibold text-hz-navy hover:border-hz-cyan">
                      {c.photoFile ? c.photoFile.name : "Subir imagen"}
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`manual-child-photo-${i}`} onChange={(e) => updateChild(i, { photoFile: e.target.files?.[0] || null })} />
                    </label>
                  </div>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-xs font-medium text-hz-ink">
                    <Checkbox data-testid={`manual-child-care-${i}`} checked={c.care_required} onCheckedChange={(v) => updateChild(i, { care_required: v === true })} />
                    Cuidado infantil
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Adultos autorizados para recogerlo *</Label>
                <div className="mt-1 flex flex-wrap gap-3">
                  {adults.map((a, ai) => (
                    <label key={ai} className="flex items-center gap-1.5 text-xs font-medium text-hz-ink">
                      <Checkbox data-testid={`manual-child-auth-${i}-${ai}`} checked={c.authorized.includes(ai)}
                        onCheckedChange={(v) => updateChild(i, { authorized: v ? [...c.authorized, ai] : c.authorized.filter((x) => x !== ai) })} />
                      {a.full_name || `Adulto ${ai + 1}`}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input placeholder="Alergias (opcional)" data-testid={`manual-child-allergies-${i}`} value={c.allergies} onChange={(e) => updateChild(i, { allergies: e.target.value })} />
                <Input placeholder="Notas médicas (opcional)" data-testid={`manual-child-medical-${i}`} value={c.medical_notes} onChange={(e) => updateChild(i, { medical_notes: e.target.value })} />
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={() => setChildren((l) => [...l, newChild()])} data-testid="manual-add-child" className="w-full border-dashed font-bold text-hz-navy">
            <Plus className="mr-2 h-4 w-4" /> Agregar niño
          </Button>
        </section>
      )}

      <section className="space-y-4 rounded-2xl border border-hz-gray/40 bg-white p-6" data-testid="manual-payment">
        <p className="font-bold text-hz-navy">Pago</p>
        <div className="grid gap-4 sm:grid-cols-4">
          <div>
            <Label className="text-xs font-semibold">Estado del pago *</Label>
            <Select value={payment.mode} onValueChange={(v) => setPayment({ ...payment, mode: v })}>
              <SelectTrigger data-testid="manual-payment-mode" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending" data-testid="manual-payment-pending">Pendiente</SelectItem>
                <SelectItem value="approved" data-testid="manual-payment-approved">Aprobado</SelectItem>
                <SelectItem value="waived" data-testid="manual-payment-waived">Cortesía</SelectItem>
                <SelectItem value="imported_confirmed" data-testid="manual-payment-imported">Importado confirmado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Monto pagado (MXN)</Label>
            <Input type="number" min="0" step="0.01" data-testid="manual-payment-amount" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} placeholder={String(total)} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Referencia</Label>
            <Input data-testid="manual-payment-reference" value={payment.reference} onChange={(e) => setPayment({ ...payment, reference: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Observaciones</Label>
            <Input data-testid="manual-payment-notes" value={payment.notes} onChange={(e) => setPayment({ ...payment, notes: e.target.value })} className="mt-1" />
          </div>
        </div>
        <div className="rounded-xl border border-hz-gray/30 bg-hz-bg/60 p-4 text-sm" data-testid="manual-breakdown">
          <p className="mb-2 font-bold text-hz-navy">Desglose del registro (solo visible para el equipo)</p>
          <ul className="space-y-1">
            {adults.map((a, i) => {
              const sub = ((a.general_event || a.dinner || a.workshops.length > 0) ? pricing.general_event?.amount || 0 : 0)
                + (a.dinner ? pricing.dinner?.amount || 0 : 0)
                + a.workshops.reduce((s, id) => s + (workshops.find((w) => w.id === id)?.price || 0), 0);
              return (
                <li key={i} className="flex justify-between" data-testid={`manual-subtotal-adult-${i}`}>
                  <span className="text-hz-ink/70">{a.full_name || `Adulto ${i + 1}`}</span>
                  <span className="font-semibold text-hz-navy">{money(sub)}</span>
                </li>
              );
            })}
            {children.filter((c) => c.care_required).length > 0 && (
              <li className="flex justify-between" data-testid="manual-subtotal-childcare">
                <span className="text-hz-ink/70">Cuidado infantil ({children.filter((c) => c.care_required).length} niño(s) · {money(careFee)} c/u)</span>
                <span className="font-semibold text-hz-navy">{money(children.filter((c) => c.care_required).length * careFee)}</span>
              </li>
            )}
            <li className="flex justify-between border-t border-hz-gray/30 pt-1" data-testid="manual-total-expected">
              <span className="font-bold text-hz-navy">Total esperado</span>
              <span className="font-extrabold text-hz-navy">{money(total)}</span>
            </li>
            {parseFloat(payment.amount) > 0 && parseFloat(payment.amount) !== total && (
              <li className="flex justify-between" data-testid="manual-difference">
                <span className="text-hz-ink/70">Diferencia (monto capturado vs esperado)</span>
                <span className="font-semibold text-hz-error">{money((parseFloat(payment.amount) || 0) - total)}</span>
              </li>
            )}
          </ul>
        </div>
        <p className="text-xs text-hz-ink/60">Si el pago está confirmado o es cortesía, se activan los accesos y se generan los QR de inmediato.</p>
        <div>
          <Label className="text-xs font-semibold">Motivo de la carga manual *</Label>
          <Input data-testid="manual-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" placeholder="Ej. Registro anticipado, carga administrativa, cortesía" />
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
          <Checkbox data-testid="manual-mark-test" checked={markTest} onCheckedChange={(v) => setMarkTest(v === true)} />
          Marcar como datos de prueba (is_test_data)
        </label>
      </section>

      <Button onClick={submit} disabled={!valid || submitting} data-testid="manual-submit-btn" className="w-full bg-hz-cyan font-bold text-white hover:bg-hz-blue disabled:opacity-60">
        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
        Crear registro
      </Button>

      {created && (
        <section className="rounded-2xl border border-hz-success/30 bg-hz-success/5 p-6" data-testid="manual-created">
          <p className="flex items-center gap-2 font-bold text-hz-success"><CheckCircle2 className="h-5 w-5" /> Registro creado: {created.group_folio}</p>
          <ul className="mt-2 space-y-1 text-sm text-hz-ink">
            {created.members.map((m) => (
              <li key={m.id} data-testid={`manual-created-member-${m.folio}`}>
                <b>{m.folio}</b> — {m.full_name} · {m.qr_token ? "QR generado" : "QR pendiente de pago"}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

const AdminCargaManual = () => (
  <AdminShell roles={["admin"]} title="Carga manual" testId="admin-carga-manual"
    subtitle="Alta administrativa de registros individuales, familiares o grupales con pago confirmado, cortesía o pendiente.">
    {() => <ManualContent />}
  </AdminShell>
);

export default AdminCargaManual;
